import { Spin } from "antd";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { FormikProps, useFormik } from "formik";
import { TFunction } from "i18next";
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";

import { IPortalPreferences } from "../../../../@types/Preferences";
import {
  IAddProtocol,
  IAddProtocolActions,
  IIntervalOption,
  IProtocolActions,
  IProtocolServerResponse,
} from "../../../../@types/Protocol";
import { IOption, SelectDropdown } from "../../../../components/SelectDropdown";
import { TextField } from "../../../../components/TextField";
import {
  AUTH_PORTAL_PREFERENCES,
  NOTIFICATION,
} from "../../../../constants/localStorageKeys";
import { useModal } from "../../../../hooks/useModal";
import { useWardLabel } from "../../../../hooks/useWardLabel";
import { BuilderLayout } from "../../../../layout/components/BuilderLayout/BuilderLayout";
import { measurementsService } from "../../../../services/measurementsService";
import { protocolService } from "../../../../services/protocol.service";
import { wardsService } from "../../../../services/wardsService";
import { mixpanelActions } from "../../../../utils/mixpanel";
import { formatActionFrequency } from "../../../../utils/protocolFrequency";
import { randomIntFromInterval } from "../../../../utils/random";
import { safeStorage, safeJsonParse } from "../../../../utils/storage";
import { spinCss } from "../../../CareInfo/components/ArticleViewer/ArticleViewer.styles";
import { ChangeWardModal } from "../ChangeWardModal/ChangeWardModal";
import { WardPublishedProtocolModal } from "../WardPublishedProtocolModal/WardPublishedProtocolModal";
import { ActionSection } from "./components/ActionSection";
import { ErrorMessage } from "./components/ActionSection/ActionSection.styles";
import {
  AddActionButton,
  BodyContainer,
  LoadingContainer,
  ScrollableContainer,
  Section,
  SectionItem,
  SectionTitle,
  selectCss,
  textFieldClassName,
} from "./ProtocolBuilder.styles";
import "dayjs/locale/en-ie";

dayjs.extend(LocalizedFormat);
dayjs.extend(localeData);
dayjs.extend(utc);
dayjs.extend(timezone);

export interface IOptionGroup {
  label: string;
  options: IOption[];
}

interface IActionRef {
  id: number;
  ref: React.RefObject<FormikProps<IProtocolActions>>;
}

// EN-5191: built from `t` rather than declared at module scope, because the labels are
// rendered. `value` stays the identity — every comparison here and in ActionSection matches
// on it, so translating a label cannot change behaviour.
const buildTypeOptions = (t: TFunction): IOption[] => [
  {
    label: t("protocols_builder_types_measurement"),
    value: "Measurement",
    key: uuidv4(),
  },
  {
    label: t("protocols_builder_types_survey"),
    value: "Survey",
    key: uuidv4(),
  },
  {
    label: t("protocols_builder_types_exerciseTests"),
    value: "ExerciseTest",
    key: uuidv4(),
  },
];

// `label` on an IOptionGroup is the group's identity (findIndex matches on it below), not
// display text — left in English deliberately.
const buildExerciseTestOptionGroup = (t: TFunction): IOptionGroup => ({
  label: "ExerciseTest",
  options: [
    {
      label: t("protocols_builder_types_sitToStand"),
      value: "OneMsts",
      key: uuidv4(),
    },
  ],
});

export function ProtocolBuilder() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Rebuilt when the language changes so the dropdown relabels with everything else.
  const typeOptions = useMemo(() => buildTypeOptions(t), [t]);
  const exerciseTestOptionGroup = useMemo(
    () => buildExerciseTestOptionGroup(t),
    [t]
  );
  const wardLabel = useWardLabel();
  const { openModal, closeModal } = useModal();

  const { protocolId } = useParams();
  const [protocol, setProtocol] = useState<IProtocolServerResponse>();

  const [isMounted, setIsMounted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingWardMeasurements, setIsLoadingWardMeasurements] =
    useState(false);
  const [whiteButtonIsLoading, setWhiteButtonIsLoading] = useState(false);
  const [purpleButtonIsLoading, setPurpleButtonIsLoading] = useState(false);

  const [actionsComponents, setActionsComponents] = useState<JSX.Element[]>([]);

  const [wardDropdownKey, setWardDropdownKey] = useState(0);
  const [wardOptions, setWardOptions] = useState<IOption[]>([]);
  const [intervalOptions, setIntervalOptions] = useState<IIntervalOption[]>([]);
  const [actionOptions, setActionOptions] = useState<IOptionGroup[]>([]);

  const [longDateFormat, setLongDateFormat] = useState<string | undefined>(
    undefined
  );

  const addActionRef = useRef<HTMLDivElement>(null);
  const actionRefs = useRef<IActionRef[]>([]);
  const isSubmittingRef = useRef(false);
  const hasLoadedProtocol = useRef(false);

  useEffect(() => {
    setIsMounted(true);

    setActionOptions([]);

    wardsService.getAvailableWards().then((wards) => {
      if (wards.data == null) return;

      const wardsToAdd: IOption[] = [];

      wards.data?.forEach((wardData) => {
        wardsToAdd.push({
          label: wardData.wardName,
          value: wardData.wardId.toString(),
          key: uuidv4(),
        });
      });

      setWardOptions(wardsToAdd);

      protocolService.getSurveyOptions().then((surveyOptions) => {
        if (surveyOptions.data == null) return;

        const surveyOptionGroups: IOptionGroup[] = [];

        wardsToAdd.forEach((ward) => {
          const optionToAdd: IOptionGroup = {
            label: `Survey ${ward.value}`,
            options: [],
          };

          surveyOptions.data?.forEach((option) => {
            if (option.wardId.toString() === ward.value)
              optionToAdd.options.push({
                label: option.surveyDisplayName,
                value: option.surveyId,
                key: uuidv4(),
              });
          });

          surveyOptionGroups.push(optionToAdd);
        });

        setActionOptions([...surveyOptionGroups, exerciseTestOptionGroup]);
        setIsLoading(false);
      });
    });

    protocolService.getIntervalOptions().then((serverOptions) => {
      if (serverOptions.data == null) return;

      const optionsToAdd: IIntervalOption[] = [];

      serverOptions.data?.forEach((option) => {
        optionsToAdd.push({
          // EN-5283: labelled from the interval type; the count is picked in its own field.
          label: formatActionFrequency(t, option.type, "X", option.displayName),
          value: option.value,
          key: uuidv4(),
          type: option.type,
        });
      });

      setIntervalOptions(optionsToAdd);
    });

    const portalPreferences = safeStorage.getItem(AUTH_PORTAL_PREFERENCES);

    if (portalPreferences) {
      const parsedPortalPreferences = safeJsonParse<
        IPortalPreferences | undefined
      >(portalPreferences, undefined);

      if (parsedPortalPreferences) {
        const { countryCode } = parsedPortalPreferences;
        if (countryCode !== "US") {
          dayjs.locale("en-ie");
        }

        // Force 24-hour format for all locales to avoid AM/PM confusion
        const dateFormat = dayjs.localeData().longDateFormat("L");
        setLongDateFormat(`${dateFormat} HH:mm`);
      }
    }

    return () => {
      setIsMounted(false);
    };
  }, []);

  const handleCancel = () => {
    mixpanelActions.track("User Action: Cancel protocol");
    navigate("/protocol");
  };

  const validateActions = () => {
    let areActionsValid = true;

    actionRefs.current.forEach((actionRef) => {
      actionRef.ref.current?.submitForm();

      areActionsValid =
        areActionsValid && (actionRef.ref.current?.isValid ?? false);
    });

    return areActionsValid;
  };

  const handleOnSubmitForm = async ({
    id,
    protocolName,
    wardId,
    published,
  }: any) => {
    if (!validateActions()) {
      isSubmittingRef.current = false;
      setPurpleButtonIsLoading(false);
      setWhiteButtonIsLoading(false);
      return;
    }

    const actions: IAddProtocolActions[] = [];

    actionRefs.current.forEach((actionRef) => {
      const formatDateForBackend = (date?: dayjs.Dayjs) => {
        if (!date) return undefined;
        return date.toISOString();
      };

      actions.push({
        ...actionRef.ref.current?.values,
        startDate: formatDateForBackend(
          actionRef.ref.current?.values.startDate
        ),
        endDate: formatDateForBackend(actionRef.ref.current?.values.endDate),
      });
    });

    const submitProtocol = async (request: IAddProtocol) => {
      try {
        if (!protocolId) {
          const result = await protocolService.addProtocol(request);

          if (result.status >= 200 && result.status < 300) {
            const notification = {
              show: true,
              message: t("protocols_toasts_created"),
              type: "success",
              width: "max-content",
            };

            safeStorage.setItem(NOTIFICATION, JSON.stringify(notification));
          } else {
            const notification = {
              show: true,
              message: t("protocols_toasts_createFailed"),
              type: "error",
              width: "max-content",
            };

            safeStorage.setItem(NOTIFICATION, JSON.stringify(notification));
          }
        } else {
          const result = await protocolService.updateProtocol(request);

          if (result.status >= 200 && result.status < 300) {
            const notification = {
              show: true,
              message: t("protocols_toasts_updated"),
              type: "success",
              width: "max-content",
            };

            safeStorage.setItem(NOTIFICATION, JSON.stringify(notification));
          } else {
            const notification = {
              show: true,
              message: t("protocols_toasts_updateFailed"),
              type: "error",
              width: "max-content",
            };

            safeStorage.setItem(NOTIFICATION, JSON.stringify(notification));
          }
        }
      } finally {
        isSubmittingRef.current = false;
        setPurpleButtonIsLoading(false);
        setWhiteButtonIsLoading(false);
      }

      navigate("/protocol");
    };

    const request: IAddProtocol = {
      id,
      protocolName,
      wardId,
      published,
      actions,
    };

    if (published) {
      mixpanelActions.track("User Action: Publish protocol");
    } else {
      mixpanelActions.track("User Action: Save protocol");
    }

    // Check if ward already has a published protocol before saving
    if (published && wardId) {
      try {
        const checkResult = await protocolService.hasPublishedProtocol(
          wardId,
          protocolId
        );

        if (checkResult.data === true) {
          isSubmittingRef.current = false;
          setPurpleButtonIsLoading(false);
          setWhiteButtonIsLoading(false);
          openModal(
            <WardPublishedProtocolModal
              variant="builder"
              onClose={() => {
                closeModal();
                // Save as unpublished after user acknowledges
                request.published = false;
                submitProtocol(request);
              }}
            />,
            {
              width: "480px",
              showCloseButton: false,
            }
          );
          return;
        }
      } catch {
        // If check fails, proceed with save and let backend handle it
      }
    }

    await submitProtocol(request);
  };

  const formValidationSchema = yup.object().shape({
    protocolName: yup
      .string()
      .max(50)
      .required(t("protocols_builder_nameRequired")),
    wardId: yup.string().required(
      t("protocols_validation_wardRequired", {
        ward: wardLabel.singularLower,
      })
    ),
  });

  const formik = useFormik<IAddProtocol>({
    initialValues: {
      id: undefined,
      protocolName: "",
      wardId: undefined,
      published: false,
    },
    onSubmit: handleOnSubmitForm,
    validationSchema: formValidationSchema,
  });

  const handleRemoveAction = (id: number) => {
    setActionsComponents((prevComponents) => {
      const activeComponent = prevComponents.find(
        (component) => component.props.isSectionActive
      );

      const tempComponents = prevComponents.filter((x) => x.props.id !== id);

      actionRefs.current = actionRefs.current.filter((ref) => ref.id !== id);

      if (tempComponents.length <= 1) {
        return tempComponents.map((component) =>
          React.cloneElement(component, {
            isCloseButtonVisible: false,
            isSectionActive: true,
          })
        );
      }

      const wasActiveRemoved = activeComponent?.props.id === id;

      if (wasActiveRemoved) {
        return tempComponents.map((component, index) =>
          React.cloneElement(component, {
            isCloseButtonVisible: true,
            isSectionActive: index === tempComponents.length - 1,
          })
        );
      }

      return tempComponents.map((component) =>
        React.cloneElement(component, {
          isCloseButtonVisible: true,
        })
      );
    });

    mixpanelActions.track("User Action: Delete Action");
  };

  const handleActiveSection = (id: number) => {
    setActionsComponents((prevComponents) =>
      prevComponents.map((component) =>
        React.cloneElement(component, {
          isSectionActive: component.props.id === id,
        })
      )
    );
  };

  const applyWardChange = (value: string | string[]) => {
    formik.setFieldValue("wardId", value as string);

    const newId = Date.now();

    const actionRef = React.createRef<FormikProps<IProtocolActions>>();

    const initialActionComponent = (
      <ActionSection
        ref={actionRef}
        key={newId}
        id={newId}
        hospitalId={formik.values.wardId}
        intervalOptions={intervalOptions}
        typeOptions={typeOptions}
        actionOptions={actionOptions}
        longDateFormat={longDateFormat}
        isCloseButtonVisible={false}
        isSectionActive
        disableNotEditableFields
        onRemove={handleRemoveAction}
        handleActiveSection={handleActiveSection}
      />
    );

    actionRefs.current = [{ id: newId, ref: actionRef }];

    setActionsComponents([initialActionComponent]);
  };

  const wardsDropdown = useMemo(() => {
    if (!isMounted) return null;

    return (
      <SelectDropdown
        key={wardDropdownKey}
        placeholder={t("protocols_selectWards", {
          wards: wardLabel.pluralLower,
        })}
        className={selectCss()}
        width={248}
        height={42}
        value={formik.values.wardId?.toString()}
        onValueChange={(value) => {
          const hasFilledActions = actionRefs.current.some(
            (actionRef) => actionRef.ref.current?.values.actionType
          );

          if (hasFilledActions) {
            openModal(
              <ChangeWardModal
                onConfirm={() => {
                  closeModal();
                  applyWardChange(value);
                }}
                onCancel={() => {
                  closeModal();
                  setWardDropdownKey((prev) => prev + 1);
                }}
              />,
              {
                width: "480px",
                showCloseButton: false,
              }
            );
            return;
          }

          applyWardChange(value);
        }}
        options={wardOptions}
      />
    );
  }, [
    formik.values.wardId,
    wardOptions,
    isMounted,
    wardDropdownKey,
    wardLabel.pluralLower,
    t,
  ]);

  useEffect(() => {
    if (formik.values.wardId === undefined) return;

    setIsLoadingWardMeasurements(true);

    measurementsService
      .getMeasurementTypesByWards([formik.values.wardId])
      .then((measurementTypeOptions) => {
        if (measurementTypeOptions.data == null) return;

        const options: IOption[] = [];

        measurementTypeOptions.data?.forEach((measurementType) => {
          options?.push({
            label: measurementType.measurementTypeDisplayName,
            value: measurementType.measurementType,
            key: uuidv4(),
          });
        });

        const optionToAdd: IOptionGroup = {
          label: "Measurement",
          options,
        };

        setActionOptions((prev) => {
          const measuremntsIndex = prev.findIndex(
            (x) => x.label === "Measurement"
          );

          if (measuremntsIndex >= 0) {
            const updated = [...prev];
            updated[measuremntsIndex] = optionToAdd;
            return updated;
          }

          return [...prev, optionToAdd];
        });

        setIsLoadingWardMeasurements(false);
      });
  }, [formik.values.wardId]);

  useEffect(() => {
    if ((isLoading && actionOptions) || protocolId !== undefined) return;

    const newId = Date.now();

    const actionRef = React.createRef<FormikProps<IProtocolActions>>();

    const initialActionComponent = (
      <ActionSection
        ref={actionRef}
        key={newId}
        id={newId}
        hospitalId={formik.values.wardId}
        intervalOptions={intervalOptions}
        typeOptions={typeOptions}
        actionOptions={actionOptions}
        longDateFormat={longDateFormat}
        isCloseButtonVisible={false}
        isSectionActive
        disableNotEditableFields
        onRemove={handleRemoveAction}
        handleActiveSection={handleActiveSection}
      />
    );

    actionRefs.current = [{ id: newId, ref: actionRef }];

    setActionsComponents([initialActionComponent]);
  }, [isLoading, formik.values.wardId, protocolId]);

  const handleAddAction = () => {
    const newId = Date.now();

    const actionRef = React.createRef<FormikProps<IProtocolActions>>();

    const newActionComponent = (
      <ActionSection
        ref={actionRef}
        key={newId}
        id={newId}
        hospitalId={formik.values.wardId}
        intervalOptions={intervalOptions}
        typeOptions={typeOptions}
        actionOptions={actionOptions}
        longDateFormat={longDateFormat}
        isCloseButtonVisible
        isSectionActive
        disableNotEditableFields
        onRemove={handleRemoveAction}
        handleActiveSection={handleActiveSection}
      />
    );

    setActionsComponents((prevComponents) => {
      const updatedComponents = prevComponents.map((component) => {
        return React.cloneElement(component, {
          isCloseButtonVisible: true,
          isSectionActive: false,
        });
      });

      actionRefs.current = [
        ...actionRefs.current,
        { id: newId, ref: actionRef },
      ];
      return [...updatedComponents, newActionComponent];
    });

    mixpanelActions.track("User Action: + Add Action");

    setTimeout(() => {
      addActionRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 0);
  };

  const handleOnSaveProtocol = async (isPublished: boolean) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setPurpleButtonIsLoading(true);
    setWhiteButtonIsLoading(true);

    await formik.setFieldValue("published", isPublished);

    // Mark main form fields as touched so validation errors are rendered
    // (the JSX guards ErrorMessage on touched.*). Skip re-validation here
    // because validateForm() runs immediately after.
    await formik.setTouched({ protocolName: true, wardId: true }, false);
    const formErrors = await formik.validateForm();
    const isFormValid = Object.keys(formErrors).length === 0;

    const areActionsValid = validateActions();

    if (!areActionsValid || !isFormValid) {
      isSubmittingRef.current = false;
      setPurpleButtonIsLoading(false);
      setWhiteButtonIsLoading(false);
      return;
    }

    formik.handleSubmit();
  };

  useEffect(() => {
    if (
      hasLoadedProtocol.current ||
      !protocolId ||
      !wardOptions.length ||
      !intervalOptions.length ||
      !actionOptions.length
    ) {
      return;
    }

    hasLoadedProtocol.current = true;

    protocolService.getProtocolById(protocolId).then((serverProtocol) => {
      const protocol = serverProtocol.data;

      setProtocol(protocol);

      formik.setValues({
        id: protocol.id,
        protocolName: protocol.protocolName,
        wardId: protocol.wardId,
        published: protocol.published,
      });

      const newComponents: JSX.Element[] = [];
      const newRefs: IActionRef[] = [];

      protocol.actions.forEach((action, index) => {
        const newId = randomIntFromInterval(1, 1000000);

        const actionRef = React.createRef<FormikProps<IProtocolActions>>();
        const newActionComponent = (
          <ActionSection
            ref={actionRef}
            key={newId}
            id={newId}
            hospitalId={protocol.wardId}
            intervalOptions={intervalOptions}
            typeOptions={typeOptions}
            actionOptions={actionOptions}
            longDateFormat={longDateFormat}
            content={action}
            isCloseButtonVisible={protocol.actions.length > 1}
            isSectionActive={index === 0}
            onRemove={handleRemoveAction}
            handleActiveSection={handleActiveSection}
          />
        );

        newRefs.push({ id: newId, ref: actionRef });
        newComponents.push(newActionComponent);
      });

      actionRefs.current = newRefs;
      setActionsComponents(newComponents);
    });
  }, [protocolId, wardOptions, intervalOptions, actionOptions]);

  return (
    <BuilderLayout
      location="Protocols"
      isPurpleButtonLoading={purpleButtonIsLoading}
      isWhiteButtonLoading={whiteButtonIsLoading}
      isLayoutLoading={isLoading}
      handleOnCancel={handleCancel}
      lastEdited={protocol?.updated || protocol?.created}
      handleOnSave={handleOnSaveProtocol}
      isPublished={protocol?.published ?? false}
    >
      <ScrollableContainer>
        <BodyContainer>
          <SectionTitle>
            {protocolId
              ? t("protocols_builder_titleEdit")
              : t("protocols_builder_titleNew")}
          </SectionTitle>

          <Section>
            <SectionItem>
              <p>{t("protocols_builder_nameLabel")} *</p>
              <TextField
                label=""
                placeholder={t("protocols_builder_namePlaceholder")}
                name="protocolName"
                value={formik.values.protocolName}
                onChange={formik.handleChange}
                className={textFieldClassName()}
                backgroudColor="white"
              />
              <ErrorMessage>
                {formik?.touched.protocolName
                  ? formik?.errors.protocolName ?? ""
                  : ""}
              </ErrorMessage>
            </SectionItem>

            <SectionItem>
              <p>{wardLabel.singular} *</p>
              {wardsDropdown}
              <ErrorMessage isWards>
                {formik?.touched.wardId ? formik?.errors.wardId ?? "" : ""}
              </ErrorMessage>
            </SectionItem>
          </Section>

          {formik.values.wardId !== undefined &&
          (!isLoadingWardMeasurements || actionsComponents.length > 0) ? (
            <>
              <SectionTitle isActionTitle>
                {t("protocols_builder_actions")}
              </SectionTitle>

              {actionsComponents.map((component) =>
                React.cloneElement(component, {
                  actionOptions,
                  intervalOptions,
                  isLoadingOptions: isLoadingWardMeasurements,
                })
              )}

              <AddActionButton
                onClick={handleAddAction}
                ref={addActionRef}
                tabIndex={0}
              >
                <p>+</p> {t("protocols_builder_addAction")}
              </AddActionButton>
            </>
          ) : null}

          {isLoadingWardMeasurements && actionsComponents.length === 0 ? (
            <LoadingContainer>
              <Spin className={spinCss()} size="large" />
            </LoadingContainer>
          ) : null}
        </BodyContainer>
      </ScrollableContainer>
    </BuilderLayout>
  );
}
